// src/components/PatientDetail.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../pages/PageHeader.jsx";
import {
  Eye,
  Download,
  FileText,
  Upload,
  X,
  Trash2,
  RefreshCcw,
} from "lucide-react";

import "../styles/patients/patientdetails.css";

import {
  fetchPatientById,
  updatePatientById,
  deletePatientById,
  // Medical history
  createMedicalHistory,
  updateMedicalHistory,
  deleteMedicalHistory,
  // Allergies
  createAllergy,
  updateAllergy,
  deleteAllergy,
  // Medications
  createMedication,
  updateMedication,
  deleteMedication,
  // Clinical Documents (sensibles)
  fetchClinicalDocuments,
  uploadClinicalDocument,
  deleteClinicalDocument,
  downloadClinicalDocument,
} from "../features/clinic/clinicThunks.js";

// Devuelve "YYYY-MM-DD" para inputs <type="date"> sin tocar TZ
const fmtDateInput = (iso) => {
  if (!iso) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
};

// Bonito para mostrar, sin romper fechas puras "YYYY-MM-DD"
const niceDate = (iso) => {
  if (!iso) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const initials = (name = "") => {
  const [a = "", b = ""] = name.trim().split(/\s+/);
  return (a[0] || "").concat(b[0] || "").toUpperCase() || "?";
};

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let value = n;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const categoryLabel = (category) => {
  if (category === "consent") return "Consentimiento";
  if (category === "analysis") return "Analítica / Informe";
  if (category === "preop") return "Preoperatorio";
  if (category === "postop") return "Postoperatorio";
  if (category === "image") return "Imagen";
  return "Otro";
};

const makeEditableList = () => ({ addOpen: false, editId: null });

const PatientDetail = () => {
  const { id } = useParams(); // id del Patient
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [deletePatientOpen, setDeletePatientOpen] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState(false);
  const [deletePatientError, setDeletePatientError] = useState(null);

  const { patientData, loading, error, clinicalDocuments } = useSelector(
    (s) => s.clinic
  );
  const { user } = useSelector((s) => s.auth);

  const isPatient = user?.role === "patient";
  const isStaff =
    user?.role === "doctor" ||
    user?.role === "receptionist" ||
    user?.role === "admin";
  const isOwner = patientData?.user?.id && user?.id === patientData.user.id;

  const canEditBasics = isStaff || isOwner; // paciente propio puede editar sus datos clínicos
  const canEditAll = isStaff; // staff (incluye admin)

  // -------- Datos clínicos (Patient) --------
  const [editBasics, setEditBasics] = useState(false);
  const [savingBasics, setSavingBasics] = useState(false);
  const [formBasics, setFormBasics] = useState({
    birth_date: "",
    gender: "",
    address: "",
    emergency_contact: "",
    notes: "",
  });

  // Medical History
  const [mhUI, setMhUI] = useState(makeEditableList());
  const [mhNew, setMhNew] = useState({
    condition: "",
    diagnosed_date: "",
    notes: "",
  });
  const [mhEdit, setMhEdit] = useState({
    condition: "",
    diagnosed_date: "",
    notes: "",
  });

  // Allergies
  const [alUI, setAlUI] = useState(makeEditableList());
  const [alNew, setAlNew] = useState({ substance: "", reaction: "" });
  const [alEdit, setAlEdit] = useState({ substance: "", reaction: "" });

  // Medications
  const [mdUI, setMdUI] = useState(makeEditableList());
  const [mdNew, setMdNew] = useState({
    name: "",
    dosage: "",
    start_date: "",
    end_date: "",
    notes: "",
  });
  const [mdEdit, setMdEdit] = useState({
    name: "",
    dosage: "",
    start_date: "",
    end_date: "",
    notes: "",
  });

  // -------- Documentos sensibles --------
  const documents = useMemo(
    () => (Array.isArray(clinicalDocuments) ? clinicalDocuments : []),
    [clinicalDocuments]
  );

  const [docCategory, setDocCategory] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const docFileInputRef = useRef(null);

  const DOC_URL_CACHE_LIMIT = 25;
  const docUrlCacheRef = useRef(new Map()); // docId -> objectUrl
  const [, setDocUrlCacheState] = useState({}); // para re-render
  const [docLoadingById, setDocLoadingById] = useState({}); // docId -> boolean

  const [previewDoc, setPreviewDoc] = useState(null); // doc object
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const resetDocForm = () => {
    setDocCategory("");
    setDocFile(null);
    if (docFileInputRef.current) docFileInputRef.current.value = "";
  };

  const clearDocUrlCache = () => {
    const map = docUrlCacheRef.current;
    for (const [, url] of map.entries()) {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    }
    docUrlCacheRef.current = new Map();
    setDocUrlCacheState({});
    setDocLoadingById({});
  };

  const setCachedDocUrl = (docId, url) => {
    const map = docUrlCacheRef.current;

    if (map.has(docId)) {
      const old = map.get(docId);
      try {
        URL.revokeObjectURL(old);
      } catch (_) {}
      map.delete(docId);
    }

    map.set(docId, url);

    while (map.size > DOC_URL_CACHE_LIMIT) {
      const firstEntry = map.entries().next().value;
      if (!firstEntry) break;
      const [oldestId, oldestUrl] = firstEntry;
      map.delete(oldestId);
      try {
        URL.revokeObjectURL(oldestUrl);
      } catch (_) {}
    }

    setDocUrlCacheState(Object.fromEntries(map.entries()));
  };

  const removeCachedDocUrl = (docId) => {
    const map = docUrlCacheRef.current;
    if (!map.has(docId)) return;
    const url = map.get(docId);
    map.delete(docId);
    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
    setDocUrlCacheState(Object.fromEntries(map.entries()));
  };

  const ensureDocUrl = async (documentId) => {
    if (!documentId) return null;

    const cached = docUrlCacheRef.current.get(documentId);
    if (cached) return cached;

    if (docLoadingById[documentId]) return null;

    setDocLoadingById((prev) => ({ ...prev, [documentId]: true }));

    const res = await dispatch(downloadClinicalDocument(documentId));

    setDocLoadingById((prev) => {
      const next = { ...prev };
      delete next[documentId];
      return next;
    });

    if (!downloadClinicalDocument.fulfilled.match(res)) return null;

    const { objectUrl } = res.payload || {};
    if (!objectUrl) return null;

    setCachedDocUrl(documentId, objectUrl);
    return objectUrl;
  };

  const loadDocuments = async () => {
    if (!id) return;
    if (!(isStaff || isOwner)) return;
    await dispatch(fetchClinicalDocuments({ patientId: id, page: 1 }));
  };

  const chooseDocFile = () => {
    if (!canEditAll) return;
    if (!docFileInputRef.current) return;
    docFileInputRef.current.click();
  };

  const submitUploadDocument = async () => {
    if (!canEditAll) return;
    if (!docFile) return;
    if (!docCategory) return;

    setUploadingDoc(true);

    const res = await dispatch(
      uploadClinicalDocument({
        patientId: id,
        file: docFile,
        category: docCategory,
        description: "",
      })
    );

    setUploadingDoc(false);

    if (uploadClinicalDocument.fulfilled.match(res)) {
      resetDocForm();
    } else {
      // eslint-disable-next-line no-console
      console.error("Error al subir documento:", res.payload);
    }
  };

  const downloadDoc = async (doc) => {
    if (!doc?.id) return;
    const filename = doc.original_filename || "documento.pdf";

    const cached = docUrlCacheRef.current.get(doc.id);
    let url = cached;

    if (!url) url = await ensureDocUrl(doc.id);
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDeleteDocument = async (documentId) => {
    if (!canEditAll) return;
    const ok = window.confirm("¿Eliminar este documento?");
    if (!ok) return;

    const res = await dispatch(deleteClinicalDocument(documentId));
    if (deleteClinicalDocument.fulfilled.match(res)) {
      removeCachedDocUrl(documentId);
      if (previewDoc?.id === documentId) {
        setPreviewDoc(null);
        setPreviewUrl(null);
        setPreviewLoading(false);
      }
    }
  };

  const openPreview = async (doc) => {
    if (!doc?.id) return;
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewLoading(true);

    const url = await ensureDocUrl(doc.id);

    setPreviewUrl(url);
    setPreviewLoading(false);
  };

  const closePreview = () => {
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewLoading(false);
  };

  const DocumentCard = ({ doc }) => {
    const filename = doc.original_filename || `Documento #${doc.id}`;

    const metaParts = [];
    if (doc.created_at) metaParts.push(niceDate(doc.created_at));
    if (typeof doc.size !== "undefined") metaParts.push(formatBytes(doc.size));
    if (doc.uploaded_by_name) metaParts.push(doc.uploaded_by_name);
    const metaText = metaParts.join(" · ");

    return (
      <div className="pd-doc-card">
        {/* Contenido de la fila */}
        <div className="pd-doc-main">
          <div className="pd-doc-icon">
            <FileText size={18} />
          </div>

          <div className="pd-doc-text">
            <div className="pd-doc-name" title={filename}>
              {filename}
            </div>

            <div className="pd-doc-sub">
              <span className="pd-doc-badge">
                {categoryLabel(doc.category)}
              </span>

              {metaText && <span className="pd-doc-meta-text">{metaText}</span>}
            </div>
          </div>
        </div>

        {/* Acciones */}
        <div className="pd-doc-actions">
          <button
            type="button"
            className="pd-icon-btn"
            onClick={() => openPreview(doc)}
            title="Ver documento"
          >
            <Eye size={16} />
          </button>

          {canEditAll && (
            <button
              type="button"
              className="pd-icon-btn pd-icon-btn--danger"
              onClick={() => handleDeleteDocument(doc.id)}
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    );
  };

  // -------- Efectos --------
  useEffect(() => {
    if (id) dispatch(fetchPatientById(id));
  }, [dispatch, id]);

  useEffect(() => {
    setEditBasics(false);
    setMhUI(makeEditableList());
    setAlUI(makeEditableList());
    setMdUI(makeEditableList());
    resetDocForm();
    clearDocUrlCache();
    closePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (patientData && String(patientData.id) === String(id)) {
      setFormBasics({
        birth_date: fmtDateInput(patientData.birth_date),
        gender: patientData.gender || "",
        address: patientData.address || "",
        emergency_contact: patientData.emergency_contact || "",
        notes: patientData.notes || "",
      });
    }
  }, [patientData, id]);

  useEffect(() => {
    if (!user) return;
    if (!patientData) return;
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, patientData, id]);

  // Cerrar modales con ESC + bloquear scroll
  useEffect(() => {
    const hasModal = !!previewDoc || deletePatientOpen;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (previewDoc) closePreview();
      else if (deletePatientOpen) closeDeletePatient();
    };

    if (hasModal) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKeyDown);

      return () => {
        document.body.style.overflow = prevOverflow;
        window.removeEventListener("keydown", onKeyDown);
      };
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDoc, deletePatientOpen]);

  // Cleanup final
  useEffect(() => {
    return () => {
      clearDocUrlCache();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- Handlers: Datos clínicos --------
  const onChangeBasics = (e) => {
    const { name, value } = e.target;
    setFormBasics((prev) => ({ ...prev, [name]: value }));
  };

  const saveBasics = async () => {
    if (!canEditBasics) return;
    setSavingBasics(true);

    const payload = {
      birth_date: formBasics.birth_date || null,
      gender: formBasics.gender || "",
      address: formBasics.address || "",
      emergency_contact: formBasics.emergency_contact || "",
      notes: formBasics.notes || "",
    };

    const res = await dispatch(updatePatientById({ id, payload }));
    setSavingBasics(false);

    if (updatePatientById.fulfilled.match(res)) {
      setEditBasics(false);
    } else {
      // eslint-disable-next-line no-console
      console.error("Error al guardar datos clínicos:", res.payload);
    }
  };

  // -------- Handlers: Medical History --------
  const startAddMH = () => {
    setMhUI({ ...mhUI, addOpen: true });
    setMhNew({ condition: "", diagnosed_date: "", notes: "" });
  };
  const cancelAddMH = () => setMhUI({ ...mhUI, addOpen: false });

  const submitAddMH = async () => {
    const data = {
      condition: mhNew.condition || "",
      diagnosed_date: mhNew.diagnosed_date || null,
      notes: mhNew.notes || "",
    };
    const res = await dispatch(createMedicalHistory({ patientId: id, data }));
    if (createMedicalHistory.fulfilled.match(res)) {
      setMhUI({ ...mhUI, addOpen: false });
    }
  };

  const startEditMH = (item) => {
    setMhUI({ addOpen: false, editId: item.id });
    setMhEdit({
      condition: item.condition || "",
      diagnosed_date: fmtDateInput(item.diagnosed_date),
      notes: item.notes || "",
    });
  };
  const cancelEditMH = () => setMhUI({ ...mhUI, editId: null });

  const submitEditMH = async () => {
    const data = {
      condition: mhEdit.condition || "",
      diagnosed_date: mhEdit.diagnosed_date || null,
      notes: mhEdit.notes || "",
    };
    const res = await dispatch(updateMedicalHistory({ id: mhUI.editId, data }));
    if (updateMedicalHistory.fulfilled.match(res)) {
      setMhUI({ ...mhUI, editId: null });
    }
  };

  const removeMH = async (mid) => {
    await dispatch(deleteMedicalHistory(mid));
  };

  // -------- Handlers: Allergies --------
  const startAddAl = () => {
    setAlUI({ ...alUI, addOpen: true });
    setAlNew({ substance: "", reaction: "" });
  };
  const cancelAddAl = () => setAlUI({ ...alUI, addOpen: false });

  const submitAddAl = async () => {
    const res = await dispatch(
      createAllergy({ patientId: id, data: { ...alNew } })
    );
    if (createAllergy.fulfilled.match(res)) {
      setAlUI({ ...alUI, addOpen: false });
    }
  };

  const startEditAl = (item) => {
    setAlUI({ addOpen: false, editId: item.id });
    setAlEdit({
      substance: item.substance || "",
      reaction: item.reaction || "",
    });
  };
  const cancelEditAl = () => setAlUI({ ...alUI, editId: null });

  const submitEditAl = async () => {
    const res = await dispatch(
      updateAllergy({ id: alUI.editId, data: { ...alEdit } })
    );
    if (updateAllergy.fulfilled.match(res)) {
      setAlUI({ ...alUI, editId: null });
    }
  };

  const removeAl = async (aid) => {
    await dispatch(deleteAllergy(aid));
  };

  // -------- Handlers: Medications --------
  const startAddMd = () => {
    setMdUI({ ...mdUI, addOpen: true });
    setMdNew({ name: "", dosage: "", start_date: "", end_date: "", notes: "" });
  };
  const cancelAddMd = () => setMdUI({ ...mdUI, addOpen: false });

  const submitAddMd = async () => {
    const payload = {
      ...mdNew,
      start_date: mdNew.start_date || null,
      end_date: mdNew.end_date || null,
    };
    const res = await dispatch(
      createMedication({ patientId: id, data: payload })
    );
    if (createMedication.fulfilled.match(res)) {
      setMdUI({ ...mdUI, addOpen: false });
    }
  };

  const startEditMd = (item) => {
    setMdUI({ addOpen: false, editId: item.id });
    setMdEdit({
      name: item.name || "",
      dosage: item.dosage || "",
      start_date: fmtDateInput(item.start_date),
      end_date: fmtDateInput(item.end_date),
      notes: item.notes || "",
    });
  };
  const cancelEditMd = () => setMdUI({ ...mdUI, editId: null });

  const submitEditMd = async () => {
    const payload = {
      ...mdEdit,
      start_date: mdEdit.start_date || null,
      end_date: mdEdit.end_date || null,
    };
    const res = await dispatch(
      updateMedication({ id: mdUI.editId, data: payload })
    );
    if (updateMedication.fulfilled.match(res)) {
      setMdUI({ ...mdUI, editId: null });
    }
  };

  const removeMd = async (mid) => {
    await dispatch(deleteMedication(mid));
  };

  // -------- Derivados --------
  const medications = useMemo(
    () => patientData?.medications ?? [],
    [patientData]
  );
  const allergies = useMemo(() => patientData?.allergies ?? [], [patientData]);
  const conditions = useMemo(
    () => patientData?.medical_conditions ?? [],
    [patientData]
  );

  // -------- Autorización rápida en cliente --------
  if (!user) return <div>Cargando sesión…</div>;
  if (loading && !patientData) return <div>Cargando…</div>;
  if (error && !patientData) return <div>Error: {error}</div>;
  if (!patientData) return null;

  if (user.role === "patient" && !isOwner) {
    return <div>No autorizado.</div>;
  };

  const openDeletePatient = () => {
    if (!isStaff) return;
    setDeletePatientError(null);
    setDeletePatientOpen(true);
  };

  const closeDeletePatient = () => {
    if (deletingPatient) return;
    setDeletePatientOpen(false);
    setDeletePatientError(null);
  };

  const confirmDeletePatient = async () => {
    if (!isStaff) return;

    setDeletePatientError(null);
    setDeletingPatient(true);

    const res = await dispatch(deletePatientById(id));

    setDeletingPatient(false);

    if (deletePatientById.fulfilled.match(res)) {
      navigate("/patients", { replace: true });
      return;
    }

    setDeletePatientError(res.payload || "No se pudo eliminar el paciente.");
  };

  return (
    <>
      <div className="pd-container">
        {!(isPatient && isOwner) && (
          <div>
            <Link to="/patients" className="pd-backlink">
              ← Volver
            </Link>
          </div>
        )}

        {/* Cabecera */}
        <div className="pd-header">
          <div className="pd-avatar-wrap">
            {patientData.avatar_url ? (
              <>
                <img
                  src={patientData.avatar_url}
                  alt={patientData.full_name}
                  className="pd-avatar"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const ph = e.currentTarget.nextSibling;
                    if (ph) ph.style.display = "flex";
                  }}
                />
                <div
                  className="pd-avatar--placeholder"
                  style={{ display: "none" }}
                  aria-hidden="true"
                >
                  {initials(patientData.full_name)}
                </div>
              </>
            ) : (
              <div className="pd-avatar--placeholder" aria-hidden="true">
                {initials(patientData.full_name)}
              </div>
            )}
          </div>

          <div className="pd-title-block">
            <h2>{patientData.full_name || `Paciente #${patientData.id}`}</h2>
            <div className="pd-subtle">
              Última cita: {niceDate(patientData.last_appointment)}
            </div>
          </div>

          {isStaff && (
            <div className="pd-actions">
              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={openDeletePatient}
                disabled={deletingPatient}
                title="Eliminar paciente"
              >
                <Trash2 size={16} />
                Eliminar
              </button>
            </div>
          )}
        </div>

        {/* Layout tipo dashboard: columna principal + columna lateral */}
        <div className="pd-layout">
          <div className="pd-layout-main">
            {/* Datos clínicos */}
            <section className="pd-section">
              <div className="pd-section__header">
                <h3 className="pd-section__title">Datos clínicos</h3>

                {canEditBasics && !editBasics && (
                  <div className="pd-actions">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => setEditBasics(true)}
                    >
                      Editar
                    </button>
                  </div>
                )}
              </div>

              <div className="pd-section__body">
                <div className="pd-form">
                  <div className="pd-field">
                    <label>Fecha de nacimiento</label>
                    <input
                      className="pd-input"
                      type="date"
                      name="birth_date"
                      value={formBasics.birth_date || ""}
                      onChange={onChangeBasics}
                      disabled={!editBasics || !canEditBasics}
                    />
                  </div>

                  <div className="pd-field">
                    <label>Género</label>
                    <input
                      className="pd-input"
                      type="text"
                      name="gender"
                      value={formBasics.gender}
                      onChange={onChangeBasics}
                      disabled={!editBasics || !canEditBasics}
                    />
                  </div>

                  <div className="pd-field">
                    <label>Contacto de emergencia</label>
                    <input
                      className="pd-input"
                      type="text"
                      name="emergency_contact"
                      value={formBasics.emergency_contact}
                      onChange={onChangeBasics}
                      disabled={!editBasics || !canEditBasics}
                    />
                  </div>

                  <div className="pd-field">
                    <label>Dirección</label>
                    <input
                      className="pd-input"
                      type="text"
                      name="address"
                      value={formBasics.address}
                      onChange={onChangeBasics}
                      disabled={!editBasics || !canEditBasics}
                    />
                  </div>

                  <div className="pd-field pd-field--full">
                    <label>Notas</label>
                    <textarea
                      className="pd-textarea"
                      name="notes"
                      rows={3}
                      value={formBasics.notes}
                      onChange={onChangeBasics}
                      disabled={!editBasics || !canEditBasics}
                    />
                  </div>
                </div>

                {canEditBasics && editBasics && (
                  <div className="pd-actions pd-actions--bottom">
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={saveBasics}
                      disabled={savingBasics}
                    >
                      {savingBasics ? "Guardando…" : "Guardar"}
                    </button>

                    <button
                      type="button"
                      className="ghost btn-sm"
                      onClick={() => {
                        setEditBasics(false);
                        setFormBasics({
                          birth_date: fmtDateInput(patientData.birth_date),
                          gender: patientData.gender || "",
                          address: patientData.address || "",
                          emergency_contact:
                            patientData.emergency_contact || "",
                          notes: patientData.notes || "",
                        });
                      }}
                      disabled={savingBasics}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* Historial médico */}
            <section className="pd-section">
              <div className="pd-section__header">
                <h3 className="pd-section__title">
                  Historial médico ({conditions.length})
                </h3>

                {canEditAll && !mhUI.addOpen && mhUI.editId == null && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={startAddMH}
                  >
                    Añadir condición
                  </button>
                )}
              </div>

              <div className="pd-section__body">
                {canEditAll && mhUI.addOpen && (
                  <div className="pd-inline-form pd-inline-form--short">
                    <input
                      className="pd-input"
                      placeholder="Condición"
                      value={mhNew.condition}
                      onChange={(e) =>
                        setMhNew({ ...mhNew, condition: e.target.value })
                      }
                    />
                    <input
                      className="pd-input"
                      type="date"
                      value={mhNew.diagnosed_date}
                      onChange={(e) =>
                        setMhNew({
                          ...mhNew,
                          diagnosed_date: e.target.value,
                        })
                      }
                    />
                    <input
                      className="pd-input"
                      placeholder="Notas"
                      value={mhNew.notes}
                      onChange={(e) =>
                        setMhNew({ ...mhNew, notes: e.target.value })
                      }
                    />

                    <div className="pd-actions">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={submitAddMH}
                      >
                        Crear
                      </button>
                      <button
                        type="button"
                        className="ghost btn-sm"
                        onClick={cancelAddMH}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                <ul className="pd-list">
                  {conditions.length === 0 && (
                    <li className="pd-status">
                      No hay condiciones registradas.
                    </li>
                  )}

                  {conditions.map((c) =>
                    mhUI.editId === c.id && canEditAll ? (
                      <li key={c.id}>
                        <div className="pd-inline-form pd-inline-form--short pd-inline-form--full">
                          <input
                            className="pd-input"
                            value={mhEdit.condition}
                            onChange={(e) =>
                              setMhEdit({
                                ...mhEdit,
                                condition: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            type="date"
                            value={mhEdit.diagnosed_date}
                            onChange={(e) =>
                              setMhEdit({
                                ...mhEdit,
                                diagnosed_date: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            value={mhEdit.notes}
                            onChange={(e) =>
                              setMhEdit({
                                ...mhEdit,
                                notes: e.target.value,
                              })
                            }
                          />
                          <div className="pd-actions">
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              onClick={submitEditMH}
                            >
                              Guardar
                            </button>
                            <button
                              type="button"
                              className="ghost btn-sm"
                              onClick={cancelEditMH}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </li>
                    ) : (
                      <li key={c.id} className="pd-list-item">
                        <div className="pd-list__row">
                          <span className="pd-list__title">
                            {c.condition}
                          </span>
                          <span className="pd-list__meta">
                            {c.diagnosed_date
                              ? `Diagnóstico: ${niceDate(c.diagnosed_date)}`
                              : ""}
                            {c.notes ? ` · ${c.notes}` : ""}
                          </span>
                        </div>

                        {canEditAll && (
                          <div className="pd-list__actions">
                            <button
                              type="button"
                              className="ghost btn-xs"
                              onClick={() => startEditMH(c)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="btn-danger btn-xs"
                              onClick={() => removeMH(c.id)}
                            >
                              Eliminar
                            </button>
                          </div>
                        )}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </section>

            {/* Medicaciones */}
            <section className="pd-section">
              <div className="pd-section__header">
                <h3 className="pd-section__title">
                  Medicaciones ({medications.length})
                </h3>

                {canEditAll && !mdUI.addOpen && mdUI.editId == null && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={startAddMd}
                  >
                    Añadir medicación
                  </button>
                )}
              </div>

              <div className="pd-section__body">
                {canEditAll && mdUI.addOpen && (
                  <div className="pd-inline-form pd-inline-form--med">
                    <input
                      className="pd-input"
                      placeholder="Nombre"
                      value={mdNew.name}
                      onChange={(e) =>
                        setMdNew({ ...mdNew, name: e.target.value })
                      }
                    />
                    <input
                      className="pd-input"
                      placeholder="Dosis"
                      value={mdNew.dosage}
                      onChange={(e) =>
                        setMdNew({ ...mdNew, dosage: e.target.value })
                      }
                    />
                    <input
                      className="pd-input"
                      type="date"
                      value={mdNew.start_date}
                      onChange={(e) =>
                        setMdNew({ ...mdNew, start_date: e.target.value })
                      }
                    />
                    <input
                      className="pd-input"
                      type="date"
                      value={mdNew.end_date}
                      onChange={(e) =>
                        setMdNew({ ...mdNew, end_date: e.target.value })
                      }
                    />
                    <input
                      className="pd-input"
                      placeholder="Notas"
                      value={mdNew.notes}
                      onChange={(e) =>
                        setMdNew({ ...mdNew, notes: e.target.value })
                      }
                    />

                    <div className="pd-actions">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={submitAddMd}
                      >
                        Crear
                      </button>
                      <button
                        type="button"
                        className="ghost btn-sm"
                        onClick={cancelAddMd}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                <ul className="pd-list">
                  {medications.length === 0 && (
                    <li className="pd-status">
                      No hay medicaciones registradas.
                    </li>
                  )}

                  {medications.map((m) =>
                    mdUI.editId === m.id && canEditAll ? (
                      <li key={m.id}>
                        <div className="pd-inline-form pd-inline-form--med pd-inline-form--full">
                          <input
                            className="pd-input"
                            placeholder="Nombre"
                            value={mdEdit.name}
                            onChange={(e) =>
                              setMdEdit({
                                ...mdEdit,
                                name: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            placeholder="Dosis"
                            value={mdEdit.dosage}
                            onChange={(e) =>
                              setMdEdit({
                                ...mdEdit,
                                dosage: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            type="date"
                            value={mdEdit.start_date}
                            onChange={(e) =>
                              setMdEdit({
                                ...mdEdit,
                                start_date: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            type="date"
                            value={mdEdit.end_date}
                            onChange={(e) =>
                              setMdEdit({
                                ...mdEdit,
                                end_date: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            placeholder="Notas"
                            value={mdEdit.notes}
                            onChange={(e) =>
                              setMdEdit({
                                ...mdEdit,
                                notes: e.target.value,
                              })
                            }
                          />

                          <div className="pd-actions">
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              onClick={submitEditMd}
                            >
                              Guardar
                            </button>
                            <button
                              type="button"
                              className="ghost btn-sm"
                              onClick={cancelEditMd}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </li>
                    ) : (
                      <li key={m.id} className="pd-list-item">
                        <div className="pd-list__row">
                          <span className="pd-list__title">
                            <strong>{m.name}</strong> · {m.dosage}
                          </span>
                          <span className="pd-list__meta">
                            {m.start_date
                              ? `Inicio: ${niceDate(m.start_date)}`
                              : ""}
                            {m.end_date ? ` · Fin: ${niceDate(m.end_date)}` : ""}
                            {m.notes ? ` · ${m.notes}` : ""}
                          </span>
                        </div>

                        {canEditAll && (
                          <div className="pd-list__actions">
                            <button
                              type="button"
                              className="ghost btn-xs"
                              onClick={() => startEditMd(m)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="btn-danger btn-xs"
                              onClick={() => removeMd(m.id)}
                            >
                              Eliminar
                            </button>
                          </div>
                        )}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </section>
          </div>

          {/* Columna lateral: documentos + alergias */}
          <div className="pd-layout-side">
            {/* Documentos sensibles */}
            {(isStaff || isOwner) && (
              <section className="pd-section">
                <div className="pd-section__header">
                  <h3 className="pd-section__title">
                    Documentos ({documents.length})
                  </h3>

                  <div className="pd-actions">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={loadDocuments}
                      title="Recargar"
                    >
                      <RefreshCcw size={16} />
                      Recargar
                    </button>
                  </div>
                </div>

                <div className="pd-section__body">
                  {canEditAll && (
                    <div className="pd-doc-upload">
                      <div className="pd-field pd-field--doccat">
                        <label>Categoría</label>
                        <select
                          className="pd-input"
                          value={docCategory}
                          onChange={(e) => setDocCategory(e.target.value)}
                        >
                          <option value="" disabled>
                            Selecciona categoría
                          </option>
                          <option value="consent">Consentimiento</option>
                          <option value="analysis">Analítica / Informe</option>
                          <option value="preop">Preoperatorio</option>
                          <option value="postop">Postoperatorio</option>
                          <option value="image">Imagen</option>
                          <option value="other">Otro</option>
                        </select>
                      </div>

                      <div className="pd-field pd-field--docfile">
                        <label>Archivo</label>
                        <div className="pd-doc-fileline">
                          <span
                            className="pd-doc-filename"
                            title={docFile?.name || ""}
                          >
                            {docFile?.name || "Ningún archivo seleccionado"}
                          </span>
                        </div>
                      </div>

                      <div className="pd-actions pd-actions--doc">
                        <button
                          className="btn-secondary btn-sm"
                          type="button"
                          onClick={chooseDocFile}
                          disabled={uploadingDoc}
                          title="Elegir"
                        >
                          <Upload size={16} />
                          Elegir
                        </button>

                        <button
                          className="btn-primary btn-sm"
                          type="button"
                          onClick={submitUploadDocument}
                          disabled={
                            uploadingDoc || !docFile || !docCategory
                          }
                          title={
                            !docCategory
                              ? "Selecciona una categoría"
                              : !docFile
                              ? "Selecciona un archivo"
                              : "Subir documento"
                          }
                        >
                          {uploadingDoc ? "Subiendo…" : "Subir"}
                        </button>

                        <button
                          className="ghost btn-sm"
                          type="button"
                          onClick={resetDocForm}
                          disabled={uploadingDoc}
                        >
                          Limpiar
                        </button>
                      </div>

                      <input
                        ref={docFileInputRef}
                        type="file"
                        accept=".pdf,application/pdf"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          setDocFile(e.target.files?.[0] || null)
                        }
                      />
                    </div>
                  )}

                  {documents.length === 0 ? (
                    <div className="pd-doc-empty">
                      <FileText size={20} />
                      <span>No hay documentos registrados.</span>
                    </div>
                  ) : (
                    <div className="pd-doc-list">
                      {documents.map((doc) => (
                        <DocumentCard key={doc.id} doc={doc} />
                      ))}
                    </div>
                  )}

                  {error && patientData && (
                    <div className="pd-substatus pd-substatus--error">
                      {error}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Alergias */}
            <section className="pd-section">
              <div className="pd-section__header">
                <h3 className="pd-section__title">
                  Alergias ({allergies.length})
                </h3>

                {canEditAll && !alUI.addOpen && alUI.editId == null && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={startAddAl}
                  >
                    Añadir alergia
                  </button>
                )}
              </div>

              <div className="pd-section__body">
                {canEditAll && alUI.addOpen && (
                  <div className="pd-inline-form pd-inline-form--short">
                    <input
                      className="pd-input"
                      placeholder="Sustancia"
                      value={alNew.substance}
                      onChange={(e) =>
                        setAlNew({
                          ...alNew,
                          substance: e.target.value,
                        })
                      }
                    />
                    <input
                      className="pd-input"
                      placeholder="Reacción"
                      value={alNew.reaction}
                      onChange={(e) =>
                        setAlNew({
                          ...alNew,
                          reaction: e.target.value,
                        })
                      }
                    />

                    <div className="pd-actions">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={submitAddAl}
                      >
                        Crear
                      </button>
                      <button
                        type="button"
                        className="ghost btn-sm"
                        onClick={cancelAddAl}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                <ul className="pd-list">
                  {allergies.length === 0 && (
                    <li className="pd-status">
                      No hay alergias registradas.
                    </li>
                  )}

                  {allergies.map((a) =>
                    alUI.editId === a.id && canEditAll ? (
                      <li key={a.id}>
                        <div className="pd-inline-form pd-inline-form--short pd-inline-form--full">
                          <input
                            className="pd-input"
                            value={alEdit.substance}
                            onChange={(e) =>
                              setAlEdit({
                                ...alEdit,
                                substance: e.target.value,
                              })
                            }
                          />
                          <input
                            className="pd-input"
                            value={alEdit.reaction}
                            onChange={(e) =>
                              setAlEdit({
                                ...alEdit,
                                reaction: e.target.value,
                              })
                            }
                          />
                          <div className="pd-actions">
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              onClick={submitEditAl}
                            >
                              Guardar
                            </button>
                            <button
                              type="button"
                              className="ghost btn-sm"
                              onClick={cancelEditAl}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </li>
                    ) : (
                      <li key={a.id} className="pd-list-item">
                        <div className="pd-list__row">
                          <span className="pd-list__title">
                            {a.substance}
                          </span>
                          <span className="pd-list__meta">
                            {a.reaction || ""}
                          </span>
                        </div>

                        {canEditAll && (
                          <div className="pd-list__actions">
                            <button
                              type="button"
                              className="ghost btn-xs"
                              onClick={() => startEditAl(a)}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              className="btn-danger btn-xs"
                              onClick={() => removeAl(a.id)}
                            >
                              Eliminar
                            </button>
                          </div>
                        )}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Modal eliminar paciente */}
      {deletePatientOpen && (
        <div className="pd-modal-backdrop" onClick={closeDeletePatient}>
          <div
            className="pd-modal pd-modal--small"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pd-modal__header">
              <div className="pd-modal__titles">
                <div className="pd-modal__title">Eliminar paciente</div>
                <div className="pd-modal__sub">
                  {patientData.full_name || `Paciente #${patientData.id}`}
                </div>
              </div>

              <button
                type="button"
                className="pd-icon-btn"
                onClick={closeDeletePatient}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X size={18} />
              </button>
            </div>

            <div className="pd-modal__body">
              {deletePatientError && (
                <div className="pd-status pd-status--error">
                  {deletePatientError}
                </div>
              )}

              <div className="pd-status">
                ¿Seguro que quieres eliminar este paciente?
              </div>
              <div className="pd-status pd-status--muted">
                Esta acción es irreversible.
              </div>
            </div>

            <div className="pd-modal__footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeletePatient}
                disabled={deletingPatient}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDeletePatient}
                disabled={deletingPatient}
              >
                <Trash2 size={16} />
                {deletingPatient ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal preview documentos clínicos */}
      {previewDoc && (
        <div className="pd-modal-backdrop" onClick={closePreview}>
          <div
            className="pd-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pd-modal__header">
              <div className="pd-modal__titles">
                <div className="pd-modal__title">
                  {previewDoc.original_filename || "Documento"}
                </div>
                <div className="pd-modal__sub">
                  {categoryLabel(previewDoc.category)}
                  {typeof previewDoc.size !== "undefined"
                    ? ` · ${formatBytes(previewDoc.size)}`
                    : ""}
                </div>
              </div>

              <button
                type="button"
                className="pd-icon-btn"
                onClick={closePreview}
                aria-label="Cerrar"
                title="Cerrar"
              >
                <X size={18} />
              </button>
            </div>

            <div className="pd-modal__body">
              {previewLoading ? (
                <div className="pd-status">
                  Cargando previsualización…
                </div>
              ) : previewUrl ? (
                <iframe
                  className="pd-doc-preview-frame"
                  title={previewDoc.original_filename || "preview"}
                  src={`${previewUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                />
              ) : (
                <div className="pd-status">
                  No se pudo previsualizar. Prueba a descargar.
                </div>
              )}
            </div>

            <div className="pd-modal__footer">
              <button
                type="button"
                className="btn-primary btn-sm"
                onClick={() => downloadDoc(previewDoc)}
              >
                <Download size={16} />
                Descargar
              </button>

              {canEditAll && (
                <button
                  type="button"
                  className="btn-danger btn-sm"
                  onClick={() => handleDeleteDocument(previewDoc.id)}
                >
                  <Trash2 size={16} />
                  Eliminar
                </button>
              )}

              <button
                type="button"
                className="ghost btn-sm"
                onClick={closePreview}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PatientDetail;